import { useState, useEffect, useCallback, useRef } from 'react'
import { AudioRecorder, RecordingResult, checkMicrophonePermission } from '../services/audio'

export interface UseAudioRecorderReturn {
  isRecording: boolean
  isPaused: boolean
  duration: number
  permissionState: PermissionState | null
  error: string | null
  startRecording: (options?: { enableVAD?: boolean; onSilenceDetected?: () => void }) => Promise<void>
  stopRecording: () => Promise<RecordingResult | null>
  cancelRecording: () => void
  requestPermission: () => Promise<void>
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [duration, setDuration] = useState(0)
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null)
  const [error, setError] = useState<string | null>(null)

  const recorderRef = useRef<AudioRecorder | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize recorder
  useEffect(() => {
    recorderRef.current = new AudioRecorder()

    // Check initial permission state
    checkMicrophonePermission().then(setPermissionState)

    return () => {
      if (recorderRef.current) {
        recorderRef.current.cleanup()
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  // Request microphone permission
  const requestPermission = useCallback(async () => {
    setError(null)
    try {
      if (!recorderRef.current) {
        recorderRef.current = new AudioRecorder()
      }
      await recorderRef.current.initialize()
      setPermissionState('granted')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get microphone permission'
      setError(message)
      setPermissionState('denied')
      throw err
    }
  }, [])

  // Start recording
  const startRecording = useCallback(async (options?: { enableVAD?: boolean; onSilenceDetected?: () => void }) => {
    setError(null)

    try {
      if (!recorderRef.current) {
        throw new Error('Recorder not initialized')
      }

      await recorderRef.current.startRecording(options)
      setIsRecording(true)
      setDuration(0)

      // Start duration counter
      intervalRef.current = setInterval(() => {
        setDuration((d) => d + 0.1)
      }, 100)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start recording'
      setError(message)
      setIsRecording(false)
      throw err
    }
  }, [])

  // Stop recording
  const stopRecording = useCallback(async (): Promise<RecordingResult | null> => {
    setError(null)

    try {
      if (!recorderRef.current) {
        console.log('[useAudioRecorder] stopRecording: no recorder')
        return null
      }

      // Check the actual recorder state, not React state
      if (!recorderRef.current.isRecording) {
        console.log('[useAudioRecorder] stopRecording: not recording')
        return null
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      console.log('[useAudioRecorder] Calling recorderRef.current.stopRecording()')
      const result = await recorderRef.current.stopRecording()
      console.log('[useAudioRecorder] stopRecording result:', !!result)
      setIsRecording(false)
      setDuration(0)

      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to stop recording'
      console.error('[useAudioRecorder] stopRecording error:', message)
      setError(message)
      setIsRecording(false)
      return null
    }
  }, [])

  // Cancel recording
  const cancelRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.cancelRecording()
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    setIsRecording(false)
    setDuration(0)
    setError(null)
  }, [])

  return {
    isRecording,
    isPaused,
    duration,
    permissionState,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    requestPermission,
  }
}
